import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
// Note: useEffect is used for loading filters from URL and handling selected lead from search
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardLayout } from '../components/DashboardLayout';
import { PipelineBoard } from '../components/PipelineBoard';
import { LeadDetailPanel } from '../components/LeadDetailPanel';
import { AddLeadModal } from '../components/AddLeadModal';
import { SearchAndFilter } from '../components/SearchAndFilter';
import { LeadsListView } from '../components/leads/LeadsListView';
import { AdvancedFilters } from '../components/leads/AdvancedFilters';
import { PipelineFilterBar } from '../components/leads/PipelineFilterBar';
import { api, User } from '../lib/api';
import { Lead } from '../types/lead';
import { LeadFilters } from '../types/filters';
import { motion } from 'framer-motion';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { LayoutGrid, List, SlidersHorizontal, Upload, Download, Calendar, AlertCircle, SearchX, RefreshCw } from 'lucide-react';
import { cn } from '../lib/utils';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../contexts/AuthContext';
import { ExportModal } from '../components/leads/export/ExportModal';
import { useToast } from '../hooks/use-toast';
import { MissedFollowupsView } from '../components/pipeline/MissedFollowupsView';

// Map ?filter=xxx URL param values to pipeline stage names
const FILTER_TO_STAGE_MAP: Record<string, string> = {
  new: 'NEW',
  contacted: 'CONTACTED',
  discussion: 'DISCUSSION',
  proposal: 'PROPOSAL',
  negotiation: 'NEGOTIATION',
  won: 'WON',
  lost: 'LOST',
  // Legacy aliases from dashboard cards
  pipeline: 'NEW',       // ?filter=pipeline → show active pipeline
  followup: 'DISCUSSION', // ?filter=followup → leads needing follow-up
  closing: 'NEGOTIATION', // ?filter=closing → leads close to closing
};

// Pipeline stages for skeleton and empty state
const PIPELINE_STAGES = ['NEW', 'CONTACTED', 'DISCUSSION', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'] as const;

const STAGE_COLORS: Record<string, string> = {
  NEW: 'bg-blue-500',
  CONTACTED: 'bg-indigo-500',
  DISCUSSION: 'bg-purple-500',
  PROPOSAL: 'bg-amber-500',
  NEGOTIATION: 'bg-orange-500',
  WON: 'bg-green-500',
  LOST: 'bg-red-500',
};

// Deterministic skeleton card counts per column
const SKELETON_CARD_COUNTS = [3, 2, 2, 1, 1, 1, 0];

// Skeleton loader for the Kanban board
const PipelineBoardSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
    <div className="flex gap-4 overflow-x-auto pb-4">
      {PIPELINE_STAGES.map((stage, idx) => (
        <div key={stage} className="flex-shrink-0 w-72">
          {/* Column header */}
          <div className="flex items-center gap-2 mb-3 px-2">
            <div className={cn('w-3 h-3 rounded-full', STAGE_COLORS[stage])} />
            <span className="text-sm font-semibold text-gray-400 uppercase">{stage}</span>
            <Skeleton className="h-5 w-6 rounded-full ml-auto" />
          </div>
          {/* Card skeletons */}
          <div className="space-y-3">
            {Array.from({ length: SKELETON_CARD_COUNTS[idx] }).map((_, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="flex items-start justify-between mb-3">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-5 w-14 rounded-full" />
                </div>
                <Skeleton className="h-4 w-40 mb-2" />
                <Skeleton className="h-4 w-28 mb-3" />
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-6 w-6 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Skeleton loader for the List view
const ListViewSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
    {/* Table header */}
    <div className="grid grid-cols-6 gap-4 p-4 border-b border-gray-200 bg-gray-50">
      {['Name', 'Company', 'Stage', 'Priority', 'Value', 'Assigned'].map((h) => (
        <span key={h} className="text-xs font-medium text-gray-400 uppercase">{h}</span>
      ))}
    </div>
    {/* Table rows */}
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="grid grid-cols-6 gap-4 p-4 border-b border-gray-100">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-5 w-20 rounded-full" />
        <Skeleton className="h-5 w-14 rounded-full" />
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-6 w-6 rounded-full" />
      </div>
    ))}
  </div>
);

// Date range quick-filter helpers
const getDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getStartOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getStartOfQuarter = (date: Date): Date => {
  const quarter = Math.floor(date.getMonth() / 3);
  return new Date(date.getFullYear(), quarter * 3, 1);
};

interface FilterState {
  stage: string[];
  status: string[];
  assignedTo: string[];
  sourceId: string[];
  riskFlag: string[];
  minValue?: number;
  maxValue?: number;
  createdAfter?: string;
  createdBefore?: string;
}

type ViewMode = 'kanban' | 'list';

export const PipelinePage: React.FC = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // State management
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showLeadDetail, setShowLeadDetail] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<FilterState>({
    stage: [],
    status: [],
    assignedTo: [],
    sourceId: [],
    riskFlag: [],
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Advanced filters state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<LeadFilters>({});

  // New FilterBar callback (stable reference to avoid re-render loops)
  const handleNewFilterParams = useCallback((params: Record<string, any>) => {
    const newFilters: LeadFilters = {};
    if (params.stage) newFilters.stages = params.stage.split(',');
    if (params.status) newFilters.priorities = params.status.split(',');
    if (params.assignedTo) newFilters.assignedTo = params.assignedTo.split(',');
    if (params.minValue) newFilters.dealValueMin = Number(params.minValue);
    if (params.maxValue) newFilters.dealValueMax = Number(params.maxValue);
    if (params.closureDateFrom) newFilters.closureDateFrom = params.closureDateFrom;
    if (params.closureDateTo) newFilters.closureDateTo = params.closureDateTo;
    if (params.search) setSearchTerm(params.search);
    setAdvancedFilters(prev => ({ ...prev, ...newFilters }));
    setShowAdvancedFilters(false);
  }, []);

  // View mode state (load from localStorage)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('pipelineViewMode');
    return (saved === 'list' || saved === 'kanban') ? saved : 'kanban';
  });

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);

  // Missed follow-ups view state
  const [showMissedFollowups, setShowMissedFollowups] = useState(false);

  // Track whether URL filters have been applied on mount
  const urlFiltersApplied = useRef(false);

  // React to ?filter=missed-followup changes even after mount (e.g. in-page nav)
  useEffect(() => {
    const filterValue = searchParams.get('filter')?.toLowerCase();
    if (filterValue === 'missed-followup') {
      setShowMissedFollowups(true);
    } else if (showMissedFollowups && filterValue !== 'missed-followup') {
      setShowMissedFollowups(false);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load filters from URL on mount and auto-apply them
  useEffect(() => {
    if (urlFiltersApplied.current) return;
    urlFiltersApplied.current = true;

    const filters: LeadFilters = {};

    // Support ?filter=missed-followup as a special dedicated view
    if (searchParams.has('filter')) {
      const filterValue = searchParams.get('filter')!.toLowerCase();
      if (filterValue === 'missed-followup') {
        setShowMissedFollowups(true);
        return; // skip normal filter processing
      } else if (filterValue === 'untouched') {
        // Untouched leads: no contact in 24+ hours — switch to list view, filter by lastContactTo=24h ago
        setViewMode('list');
        const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);
        filters.lastContactTo = cutoff.toISOString().split('T')[0];
        // Exclude WON/LOST
        filters.stages = ['NEW', 'CONTACTED', 'DISCUSSION', 'PROPOSAL', 'NEGOTIATION'];
      } else {
        setShowMissedFollowups(false);
      }
      // Support ?filter=new (lowercase) by mapping to stage
      const mappedStage = FILTER_TO_STAGE_MAP[filterValue];
      if (mappedStage) {
        filters.stages = [mappedStage];
      }
    }

    // Support ?stage=WON (singular alias from dashboard cards)
    if (searchParams.has('stage') && !searchParams.has('stages')) {
      const singleStage = searchParams.get('stage')!.toUpperCase();
      if (singleStage) {
        const existing = filters.stages || [];
        filters.stages = Array.from(new Set([...existing, singleStage]));
      }
    }

    // Support ?priority=HOT (singular alias from dashboard cards)
    if (searchParams.has('priority') && !searchParams.has('priorities')) {
      const singlePriority = searchParams.get('priority')!.toUpperCase();
      if (singlePriority) filters.priorities = [singlePriority];
    }

    // Support ?stages=NEW,CONTACTED (existing format)
    if (searchParams.has('stages')) {
      const stagesFromUrl = searchParams.get('stages')!.split(',');
      // Merge with any stages from ?filter param
      const existingStages = filters.stages || [];
      const mergedStages = Array.from(new Set([...existingStages, ...stagesFromUrl]));
      filters.stages = mergedStages;
    }

    if (searchParams.has('entryDateFrom')) filters.entryDateFrom = searchParams.get('entryDateFrom')!;
    if (searchParams.has('entryDateTo')) filters.entryDateTo = searchParams.get('entryDateTo')!;
    if (searchParams.has('closureDateFrom')) filters.closureDateFrom = searchParams.get('closureDateFrom')!;
    if (searchParams.has('closureDateTo')) filters.closureDateTo = searchParams.get('closureDateTo')!;
    if (searchParams.has('lastContactFrom')) filters.lastContactFrom = searchParams.get('lastContactFrom')!;
    if (searchParams.has('lastContactTo')) filters.lastContactTo = searchParams.get('lastContactTo')!;
    if (searchParams.has('priorities')) filters.priorities = searchParams.get('priorities')!.split(',');
    if (searchParams.has('assignedTo')) filters.assignedTo = searchParams.get('assignedTo')!.split(',');
    if (searchParams.has('dealValueMin')) filters.dealValueMin = parseFloat(searchParams.get('dealValueMin')!);
    if (searchParams.has('dealValueMax')) filters.dealValueMax = parseFloat(searchParams.get('dealValueMax')!);
    if (searchParams.has('riskLevel')) filters.riskLevel = searchParams.get('riskLevel') as LeadFilters['riskLevel'];

    if (Object.keys(filters).length > 0) {
      // Auto-apply: set filters directly into advancedFilters state
      // This feeds into apiParams immediately, triggering the query with filters
      setAdvancedFilters(filters);
      setShowAdvancedFilters(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if there's a selected lead from search
  useEffect(() => {
    if (location.state && (location.state as any).selectedLeadId) {
      const leadId = (location.state as any).selectedLeadId;
      // Fetch the lead details and open the detail panel
      api.getLead(leadId).then(lead => {
        setSelectedLead(lead);
        setShowLeadDetail(true);
      }).catch(error => {
        console.error('Failed to fetch lead:', error);
      });
    }
  }, [location.state]);

  // Build API parameters (merge basic and advanced filters)
  // Memoized to prevent unnecessary React Query re-fetches on every render
  const apiParams = useMemo(() => ({
    query: searchTerm || undefined,
    // Basic filters from SearchAndFilter
    stage: activeFilters.stage.length > 0 ? activeFilters.stage.join(',') :
           (advancedFilters.stages && advancedFilters.stages.length > 0 ? advancedFilters.stages.join(',') : undefined),
    status: activeFilters.status.length > 0 ? activeFilters.status.join(',') :
            (advancedFilters.priorities && advancedFilters.priorities.length > 0 ? advancedFilters.priorities.join(',') : undefined),
    assignedTo: activeFilters.assignedTo.length > 0 ? activeFilters.assignedTo.join(',') :
                (advancedFilters.assignedTo && advancedFilters.assignedTo.length > 0 ? advancedFilters.assignedTo.join(',') : undefined),
    sourceId: activeFilters.sourceId.length > 0 ? activeFilters.sourceId.join(',') : undefined,
    sortBy,
    sortOrder,
    minValue: activeFilters.minValue || advancedFilters.dealValueMin,
    maxValue: activeFilters.maxValue || advancedFilters.dealValueMax,
    createdAfter: activeFilters.createdAfter || advancedFilters.entryDateFrom,
    createdBefore: activeFilters.createdBefore || advancedFilters.entryDateTo,
    // Advanced filters
    closureDateFrom: advancedFilters.closureDateFrom,
    closureDateTo: advancedFilters.closureDateTo,
    lastContactFrom: advancedFilters.lastContactFrom,
    lastContactTo: advancedFilters.lastContactTo,
    riskLevel: advancedFilters.riskLevel,
    limit: 200,
  }), [
    searchTerm,
    activeFilters.stage, activeFilters.status, activeFilters.assignedTo,
    activeFilters.sourceId, activeFilters.minValue, activeFilters.maxValue,
    activeFilters.createdAfter, activeFilters.createdBefore,
    sortBy, sortOrder,
    advancedFilters.stages, advancedFilters.priorities, advancedFilters.assignedTo,
    advancedFilters.dealValueMin, advancedFilters.dealValueMax,
    advancedFilters.entryDateFrom, advancedFilters.entryDateTo,
    advancedFilters.closureDateFrom, advancedFilters.closureDateTo,
    advancedFilters.lastContactFrom, advancedFilters.lastContactTo,
    advancedFilters.riskLevel,
  ]);

  // Determine if any filters are active (for empty state messaging)
  const hasActiveFilters = useMemo(() => {
    return (
      searchTerm.length > 0 ||
      activeFilters.stage.length > 0 ||
      activeFilters.status.length > 0 ||
      activeFilters.assignedTo.length > 0 ||
      activeFilters.sourceId.length > 0 ||
      activeFilters.riskFlag.length > 0 ||
      activeFilters.minValue !== undefined ||
      activeFilters.maxValue !== undefined ||
      activeFilters.createdAfter !== undefined ||
      activeFilters.createdBefore !== undefined ||
      (advancedFilters.stages && advancedFilters.stages.length > 0) ||
      (advancedFilters.priorities && advancedFilters.priorities.length > 0) ||
      (advancedFilters.assignedTo && advancedFilters.assignedTo.length > 0) ||
      advancedFilters.dealValueMin !== undefined ||
      advancedFilters.dealValueMax !== undefined ||
      advancedFilters.entryDateFrom !== undefined ||
      advancedFilters.entryDateTo !== undefined ||
      advancedFilters.closureDateFrom !== undefined ||
      advancedFilters.closureDateTo !== undefined ||
      advancedFilters.lastContactFrom !== undefined ||
      advancedFilters.lastContactTo !== undefined ||
      advancedFilters.riskLevel !== undefined
    );
  }, [searchTerm, activeFilters, advancedFilters]);

  // Clear all filters handler
  const handleClearAllFilters = useCallback(() => {
    setSearchTerm('');
    setActiveFilters({
      stage: [],
      status: [],
      assignedTo: [],
      sourceId: [],
      riskFlag: [],
    });
    setAdvancedFilters({});
    setShowAdvancedFilters(false);
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  // Fetch leads with placeholderData to keep previous results visible during refetch
  const {
    data: leads = [],
    isLoading: leadsLoading,
    isFetching: leadsFetching,
    isError,
    error,
    refetch: refetchLeads
  } = useQuery({
    queryKey: ['leads', apiParams],
    queryFn: () => api.getLeads(apiParams),
    staleTime: 30000,
    placeholderData: (previousData) => previousData,
  });

  // Fetch all users for list view assignment
  const { data: allUsers = [] } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: () => api.getUsers(),
    staleTime: 60000,
  });

  // Mutations
  const updateLeadMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updateLead(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setShowLeadDetail(false);
      setSelectedLead(null);
      toast({ title: 'Lead updated successfully' });
    },
    onError: (error: any) => {
      console.error('Failed to update lead:', error);
      toast({ title: 'Failed to update lead', description: error.message || 'Unknown error. Please try again.', variant: 'destructive' });
    },
  });

  const deleteLeadMutation = useMutation({
    mutationFn: (id: string) => api.deleteLead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setShowLeadDetail(false);
      setSelectedLead(null);
      toast({ title: 'Lead deleted successfully' });
    },
    onError: (error: any) => {
      console.error('Failed to delete lead:', error);
      toast({ title: 'Failed to delete lead', description: error.message || 'Unknown error. Please try again.', variant: 'destructive' });
    },
  });

  // Event handlers
  const handleLeadMove = (leadId: string, newStage: Lead['stage']) => {
    updateLeadMutation.mutate({ id: leadId, data: { stage: newStage } });
  };

  const handleLeadClick = (lead: Lead) => {
    setSelectedLead(lead);
    setShowLeadDetail(true);
  };

  const handleLeadUpdate = (updatedLead: Lead) => {
    updateLeadMutation.mutate({ id: updatedLead.id, data: updatedLead });
  };

  const handleLeadCreated = (newLead: Lead) => {
    queryClient.invalidateQueries({ queryKey: ['leads'] });
    setShowAddLeadModal(false);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleFilterChange = (filters: FilterState) => {
    setActiveFilters(filters);
  };

  const handleSortChange = (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  const handleDeleteLead = (leadId: string) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      deleteLeadMutation.mutate(leadId);
    }
  };

  // View mode handlers
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('pipelineViewMode', mode);
  };

  // Advanced filters handlers
  const handleAdvancedFiltersChange = (filters: LeadFilters) => {
    setAdvancedFilters(filters);
    // Update URL params
    updateUrlParams(filters);
  };

  const handleApplyAdvancedFilters = () => {
    // Filters are already applied via state, just trigger refetch
    refetchLeads();
  };

  const updateUrlParams = (filters: LeadFilters) => {
    const params = new URLSearchParams();

    if (filters.entryDateFrom) params.set('entryDateFrom', filters.entryDateFrom);
    if (filters.entryDateTo) params.set('entryDateTo', filters.entryDateTo);
    if (filters.closureDateFrom) params.set('closureDateFrom', filters.closureDateFrom);
    if (filters.closureDateTo) params.set('closureDateTo', filters.closureDateTo);
    if (filters.lastContactFrom) params.set('lastContactFrom', filters.lastContactFrom);
    if (filters.lastContactTo) params.set('lastContactTo', filters.lastContactTo);
    if (filters.stages && filters.stages.length > 0) params.set('stages', filters.stages.join(','));
    if (filters.priorities && filters.priorities.length > 0) params.set('priorities', filters.priorities.join(','));
    if (filters.assignedTo && filters.assignedTo.length > 0) params.set('assignedTo', filters.assignedTo.join(','));
    if (filters.dealValueMin !== undefined) params.set('dealValueMin', filters.dealValueMin.toString());
    if (filters.dealValueMax !== undefined) params.set('dealValueMax', filters.dealValueMax.toString());
    if (filters.riskLevel) params.set('riskLevel', filters.riskLevel);

    setSearchParams(params, { replace: true });
  };

  // Bulk action handlers for list view
  const handleBulkAssign = async (leadIds: string[], userId: string) => {
    for (const leadId of leadIds) {
      await updateLeadMutation.mutateAsync({ id: leadId, data: { assignedTo: userId } });
    }
    queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  const handleBulkChangeStage = async (leadIds: string[], stage: Lead['stage']) => {
    for (const leadId of leadIds) {
      await updateLeadMutation.mutateAsync({ id: leadId, data: { stage } });
    }
    queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  const handleBulkChangePriority = async (leadIds: string[], priority: Lead['priority']) => {
    for (const leadId of leadIds) {
      await updateLeadMutation.mutateAsync({ id: leadId, data: { priority } });
    }
    queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  const handleBulkDelete = async (leadIds: string[]) => {
    for (const leadId of leadIds) {
      await deleteLeadMutation.mutateAsync(leadId);
    }
    queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  // List view specific update handler
  const handleListLeadUpdate = async (leadId: string, data: Partial<Lead>) => {
    await updateLeadMutation.mutateAsync({ id: leadId, data });
  };

  // --- Error State (full-page, only when we have NO cached data to show) ---
  if (isError && !leads.length) {
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="bg-red-50 rounded-full p-4 mb-6">
            <AlertCircle className="w-12 h-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Failed to Load Pipeline</h2>
          <p className="text-gray-500 mb-2 text-center max-w-md">
            We couldn't load your leads. This could be a network issue or the server may be temporarily unavailable.
          </p>
          <p className="text-sm text-red-500 mb-6 font-mono bg-red-50 px-3 py-1 rounded">
            {errorMessage}
          </p>
          <button
            onClick={() => refetchLeads()}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {viewMode === 'kanban' ? 'Pipeline Board' : 'Leads List'}
            </h1>
            <p className="text-gray-600 mt-1">
              Manage and track your leads through the sales pipeline
              {!leadsLoading && leads.length > 0 && (
                <span className="ml-2 text-sm font-medium text-gray-500">
                  ({leads.length} lead{leads.length !== 1 ? 's' : ''})
                </span>
              )}
              {leadsFetching && !leadsLoading && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full font-medium">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Updating...
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1" role="tablist" aria-label="View mode">
              <button
                role="tab"
                aria-selected={viewMode === 'kanban'}
                aria-controls="pipeline-view"
                onClick={() => handleViewModeChange('kanban')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                  viewMode === 'kanban'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                )}
              >
                <LayoutGrid className="w-4 h-4" />
                <span className="hidden sm:inline">Kanban</span>
              </button>
              <button
                role="tab"
                aria-selected={viewMode === 'list'}
                aria-controls="pipeline-view"
                onClick={() => handleViewModeChange('list')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                  viewMode === 'list'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                )}
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>

            <button
              onClick={() => navigate('/pipeline?filter=missed-followup')}
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors',
                showMissedFollowups
                  ? 'bg-red-50 text-red-700 border-red-300'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              )}
            >
              Missed Follow-ups
            </button>

            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors',
                showAdvancedFilters
                  ? 'bg-blue-50 text-blue-700 border-blue-300'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              )}
            >
              <SlidersHorizontal className="w-4 h-4" />
              {showAdvancedFilters ? 'Hide Filters' : 'Show Filters'}
            </button>

            <button
              onClick={() => navigate('/leads/import')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Upload className="w-4 h-4" />
              Import
            </button>

            <button
              onClick={() => setShowExportModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAddLeadModal(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Add New Lead
            </motion.button>
          </div>
        </div>

        {/* New Filter Bar (HubSpot-style) */}
        <PipelineFilterBar
          onFilterParamsChange={handleNewFilterParams}
          users={allUsers?.map((u: any) => ({ id: u.id, name: u.name, role: u.role, email: u.email })) || []}
        />

        {/* Legacy Advanced Filters (hidden when new filter bar is active) */}
        {showAdvancedFilters && (
          <AdvancedFilters
            filters={advancedFilters}
            onFiltersChange={handleAdvancedFiltersChange}
            onApply={handleApplyAdvancedFilters}
            allUsers={allUsers}
            userId={user?.id || ''}
          />
        )}

        {/* Quick Date Presets */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mr-1">
            <Calendar className="w-4 h-4" />
            <span className="font-medium">Quick filters:</span>
          </div>
          {([
            { label: 'Today', getRange: () => {
              const today = getDateString(new Date());
              return { from: today, to: today };
            }},
            { label: 'This Week', getRange: () => {
              const today = new Date();
              return { from: getDateString(getStartOfWeek(today)), to: getDateString(today) };
            }},
            { label: 'This Month', getRange: () => {
              const today = new Date();
              const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
              return { from: getDateString(firstOfMonth), to: getDateString(today) };
            }},
            { label: 'This Quarter', getRange: () => {
              const today = new Date();
              return { from: getDateString(getStartOfQuarter(today)), to: getDateString(today) };
            }},
          ] as const).map((preset) => {
            const range = preset.getRange();
            const isActive = advancedFilters.entryDateFrom === range.from && advancedFilters.entryDateTo === range.to;
            return (
              <button
                key={preset.label}
                onClick={() => {
                  const newFilters: LeadFilters = {
                    ...advancedFilters,
                    entryDateFrom: range.from,
                    entryDateTo: range.to,
                  };
                  setAdvancedFilters(newFilters);
                  updateUrlParams(newFilters);
                }}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors',
                  isActive
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                )}
              >
                {preset.label}
              </button>
            );
          })}
          {(advancedFilters.entryDateFrom || advancedFilters.entryDateTo) && (
            <button
              onClick={() => {
                const newFilters: LeadFilters = {
                  ...advancedFilters,
                  entryDateFrom: undefined,
                  entryDateTo: undefined,
                };
                setAdvancedFilters(newFilters);
                updateUrlParams(newFilters);
              }}
              className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Clear dates
            </button>
          )}
        </div>

        {/* Search and Filters */}
        <SearchAndFilter
          searchTerm={searchTerm}
          onSearch={handleSearch}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          sortBy={sortBy}
          sortOrder={sortOrder}
          leads={leads}
        />

        {/* Background error banner - shown when refetch fails but we have stale data */}
        {isError && leads.length > 0 && (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800 flex-1">
              Failed to refresh data. Showing previously loaded results.
            </p>
            <button
              onClick={() => refetchLeads()}
              className="flex items-center gap-1.5 text-sm font-medium text-amber-700 hover:text-amber-900 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        )}

        {/* Missed Follow-ups View */}
        {showMissedFollowups && <MissedFollowupsView />}

        {/* Conditional View Rendering */}
        {!showMissedFollowups && (
        <div id="pipeline-view" role="tabpanel" aria-label={viewMode === 'kanban' ? 'Kanban board view' : 'List view'}>

        {/* Loading skeleton (initial load only, not background refetches) */}
        {leadsLoading ? (
          viewMode === 'kanban' ? <PipelineBoardSkeleton /> : <ListViewSkeleton />
        ) : leads.length === 0 && !leadsFetching ? (
          /* Empty state */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <div className="bg-gray-100 rounded-full p-4 mb-5">
                <SearchX className="w-10 h-10 text-gray-400" />
              </div>
              {hasActiveFilters ? (
                <>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No leads match your filters</h3>
                  <p className="text-gray-500 text-center max-w-sm mb-6">
                    Try adjusting your search terms or filters to find what you are looking for.
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleClearAllFilters}
                      className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Clear All Filters
                    </button>
                    <button
                      onClick={() => refetchLeads()}
                      className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Refresh
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No leads yet</h3>
                  <p className="text-gray-500 text-center max-w-sm mb-6">
                    Get started by adding your first lead to the pipeline.
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowAddLeadModal(true)}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    Add Your First Lead
                  </motion.button>
                </>
              )}
            </div>
          </div>
        ) : (
          /* Data loaded - render board or list with subtle opacity during background refetch */
          viewMode === 'kanban' ? (
            <div className={cn(
              'bg-white rounded-lg shadow-sm border border-gray-200 transition-opacity duration-200',
              leadsFetching && !leadsLoading ? 'opacity-80' : 'opacity-100'
            )}>
              <PipelineBoard
                leads={leads}
                onLeadMove={handleLeadMove}
                onLeadClick={handleLeadClick}
                onAddLead={() => setShowAddLeadModal(true)}
                onDeleteLead={handleDeleteLead}
              />
            </div>
          ) : (
            <div className={cn(
              'transition-opacity duration-200',
              leadsFetching && !leadsLoading ? 'opacity-80' : 'opacity-100'
            )}>
              <LeadsListView
                leads={leads}
                allUsers={allUsers}
                onLeadUpdate={handleListLeadUpdate}
                onLeadDelete={handleDeleteLead}
                onLeadClick={handleLeadClick}
                onBulkAssign={handleBulkAssign}
                onBulkChangeStage={handleBulkChangeStage}
                onBulkChangePriority={handleBulkChangePriority}
                onBulkDelete={handleBulkDelete}
              />
            </div>
          )
        )}
        </div>
        )} {/* end !showMissedFollowups */}

        {/* Lead Detail Panel */}
        {showLeadDetail && selectedLead && (
          <LeadDetailPanel
            lead={selectedLead}
            isOpen={showLeadDetail}
            onClose={() => {
              setShowLeadDetail(false);
              setSelectedLead(null);
            }}
            onLeadUpdate={handleLeadUpdate}
          />
        )}

        {/* Add Lead Modal */}
        {showAddLeadModal && (
          <AddLeadModal
            isOpen={showAddLeadModal}
            onClose={() => setShowAddLeadModal(false)}
            onLeadCreated={handleLeadCreated}
          />
        )}

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          currentFilters={advancedFilters}
          totalLeads={leads.length}
        />
      </div>
    </DashboardLayout>
  );
};
